/**
 * RhPlanningsPanel — vue agrégée « Plannings » (LOT B).
 * Mode semaine : créneaux de tous les services, en ligne par agent.
 * Mode année : présence/absences d'une personne sur toutes ses briques.
 * Conforme mockup_rh_global_agrege.
 */
import { useState, useEffect } from 'react';
import { Card, Alert } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { RhWho, RhServicePill, SERVICE_COLORS } from './rh-ui';
import type { RhPlanningsAgreges, RhAgentFiltrable, RhPresenceAnnee } from '@roronoa-collectivites/shared-types';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_COURT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

function lundi(d: Date): Date { const x = new Date(d); const j = x.getDay() === 0 ? 7 : x.getDay(); x.setDate(x.getDate() - (j - 1)); x.setHours(0, 0, 0, 0); return x; }
function ymd(d: Date): string { return d.toISOString().slice(0, 10); }

export function RhPlanningsPanel() {
  const [mode, setMode] = useState<'semaine' | 'annee'>('semaine');
  const [weekStart, setWeekStart] = useState(() => ymd(lundi(new Date())));
  const [filtres, setFiltres] = useState<{ service?: string; agentId?: string }>({});
  const [data, setData] = useState<RhPlanningsAgreges | null>(null);
  const [meta, setMeta] = useState<{ agents: RhAgentFiltrable[]; services: string[]; managers: Array<{ id: string; nom: string }> } | null>(null);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [personneId, setPersonneId] = useState<string>('');
  const [presence, setPresence] = useState<RhPresenceAnnee | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.rh.agentsFiltrables().then(setMeta).catch(() => {}); }, []);

  useEffect(() => {
    if (mode !== 'semaine') return;
    setLoading(true); setError(null);
    api.rh.planningsAgreges(weekStart, filtres).then(setData)
      .catch((e) => setError(e instanceof ApiException ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [mode, weekStart, filtres]);

  useEffect(() => {
    if (mode !== 'annee' || !personneId) return;
    setLoading(true); setError(null);
    api.rh.presenceAnneeAgregee(personneId, annee).then(setPresence)
      .catch((e) => setError(e instanceof ApiException ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [mode, personneId, annee]);

  function decalerSemaine(n: number) { const d = new Date(`${weekStart}T00:00:00`); d.setDate(d.getDate() + n * 7); setWeekStart(ymd(d)); }

  // Regroupe les créneaux par agent puis par jour (index 0-6)
  const parAgent = new Map<string, { agent: RhAgentFiltrable; jours: RhPlanningsAgreges['creneaux'][] }>();
  if (data) {
    for (const a of data.agents) parAgent.set(a.id, { agent: a, jours: [[], [], [], [], [], [], []] });
    const ws = new Date(`${data.weekStart}T00:00:00`);
    for (const c of data.creneaux) {
      const jour = Math.floor((new Date(c.dateDebut).getTime() - ws.getTime()) / 86400000);
      if (jour >= 0 && jour < 7) parAgent.get(c.agentUserId)?.jours[jour]!.push(c);
    }
  }

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Barre : mode + filtres */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button type="button" onClick={() => setMode('semaine')} className={`rounded-r-md px-3 py-1.5 text-sm ${mode === 'semaine' ? 'bg-rh text-white' : 'text-ink-muted'}`}>Semaine — plannings</button>
            <button type="button" onClick={() => setMode('annee')} className={`rounded-r-md px-3 py-1.5 text-sm ${mode === 'annee' ? 'bg-rh text-white' : 'text-ink-muted'}`}>Année d'une personne</button>
          </div>

          {mode === 'semaine' ? (
            <>
              <select value={filtres.service ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, service: e.target.value || undefined }))}
                className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">Tous les services</option>
                {meta?.services.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filtres.agentId ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, agentId: e.target.value || undefined }))}
                className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">Tous les agents</option>
                {meta?.agents.map((a) => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
              </select>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => decalerSemaine(-1)} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">←</button>
                <span className="text-sm text-ink">Semaine du {new Date(`${weekStart}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                <button type="button" onClick={() => decalerSemaine(1)} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">→</button>
              </div>
            </>
          ) : (
            <>
              <select value={personneId} onChange={(e) => setPersonneId(e.target.value)}
                className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">Choisir une personne…</option>
                {meta?.agents.map((a) => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
              </select>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => setAnnee((a) => a - 1)} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">←</button>
                <span className="text-sm font-medium text-ink tabular-nums">{annee}</span>
                <button type="button" onClick={() => setAnnee((a) => a + 1)} className="rounded-r-md border border-border px-2 py-1 text-ink-muted hover:text-ink">→</button>
              </div>
            </>
          )}
        </div>
      </Card>

      {loading && <p className="text-sm text-ink-muted">Chargement…</p>}

      {/* MODE SEMAINE */}
      {mode === 'semaine' && data && !loading && (
        parAgent.size === 0 ? <Card className="p-6"><p className="text-center text-sm text-ink-subtle">Aucun créneau cette semaine pour ce filtre.</p></Card> : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="px-3 py-2.5 font-medium" style={{ minWidth: 160 }}>Agent</th>
                  {JOURS.map((j) => <th key={j} className="px-2 py-2.5 text-center font-medium">{j}</th>)}
                </tr>
              </thead>
              <tbody>
                {[...parAgent.values()].map(({ agent, jours }) => (
                  <tr key={agent.id} className="border-b border-border/60 last:border-0 align-top">
                    <td className="px-3 py-2.5"><RhWho nom={`${agent.prenom} ${agent.nom}`} /></td>
                    {jours.map((cs, i) => (
                      <td key={i} className="px-1.5 py-2 align-top">
                        <div className="space-y-1">
                          {cs.map((c) => (
                            <div key={c.id} className="rounded-r-sm px-1.5 py-1 text-[11px] leading-tight text-white" style={{ background: SERVICE_COLORS[c.service] ?? 'var(--ink-subtle)' }}>
                              <div className="font-medium">{new Date(c.dateDebut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                              <div className="truncate opacity-90">{c.titre}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        )
      )}

      {/* MODE ANNÉE PERSONNE */}
      {mode === 'annee' && !loading && (
        !personneId ? <Card className="p-6"><p className="text-center text-sm text-ink-subtle">Choisissez une personne pour voir son année.</p></Card> :
        presence && (
        <>
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))' }}>
            <StatBox label="Jours de présence" value={presence.compteurs.presence ?? 0} tone="ok" />
            <StatBox label="Congés" value={presence.compteurs.conges ?? 0} />
            <StatBox label="Absences" value={presence.compteurs.absences ?? 0} tone="warn" />
          </div>
          <Card className="overflow-hidden p-0">
            <div className="border-b border-border px-4 py-3"><h3 className="font-display text-sm font-semibold text-ink">{presence.agent.nom} — {annee}</h3></div>
            <AnneePivot jours={presence.jours} annee={annee} />
          </Card>
        </>
        )
      )}
    </div>
  );
}

function StatBox({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  const c = tone === 'ok' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-ink';
  return <div className="rounded-r-lg border border-border bg-surface px-3.5 py-3"><div className="text-[11px] text-ink-subtle">{label}</div><div className={`mt-0.5 text-2xl font-bold ${c}`}>{value}</div></div>;
}

// Grille annuelle : mois en colonnes, jours en lignes (comme les autres briques)
function AnneePivot({ jours, annee }: { jours: RhPresenceAnnee['jours']; annee: number }) {
  const byKey = new Map(jours.map((j) => [j.date, j]));
  const couleur = (s: string) => s === 'presence' ? 'var(--c-success, #3fae74)' : s === 'conge' ? 'var(--c-info, #4d90d8)' : s === 'absence' ? 'var(--c-warn, #d99a28)' : 'transparent';
  return (
    <div className="overflow-x-auto p-3">
      <table className="text-[10px]">
        <thead>
          <tr><th className="px-1 py-1 text-ink-subtle"></th>{MOIS_COURT.map((m) => <th key={m} className="px-1 py-1 text-center font-medium text-ink-subtle" style={{ minWidth: 26 }}>{m}</th>)}</tr>
        </thead>
        <tbody>
          {Array.from({ length: 31 }, (_, di) => (
            <tr key={di}>
              <td className="px-1 py-0.5 text-right text-ink-subtle tabular-nums">{di + 1}</td>
              {MOIS_COURT.map((_, mi) => {
                const d = new Date(annee, mi, di + 1);
                if (d.getMonth() !== mi) return <td key={mi} />;
                const iso = d.toISOString().slice(0, 10);
                const jour = byKey.get(iso);
                const c = jour ? couleur(jour.statut) : 'transparent';
                return <td key={mi} className="px-0.5 py-0.5"><div className="h-3.5 w-3.5 rounded-sm" style={{ background: c, border: c === 'transparent' ? '1px solid var(--border)' : 'none' }} title={jour?.libelle} /></td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-ink-muted">
        <Legend c="var(--c-success, #3fae74)" t="Présence" />
        <Legend c="var(--c-info, #4d90d8)" t="Congés" />
        <Legend c="var(--c-warn, #d99a28)" t="Absence" />
      </div>
    </div>
  );
}
function Legend({ c, t }: { c: string; t: string }) { return <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: c }} />{t}</span>; }
